import { generateClient } from "aws-amplify/api";
import {
	FieldContinuationDataForResolver,
	FieldTemplate,
	FormTemplate,
	FormTemplateForResolver,
	FieldTemplateForResolver,
	Option,
	JsonStructure,
	FieldContinuationData,
} from "db/definitions";
import { recordSentry } from "utils/recordSentry";
import { db } from "../db";
import { bulkGetFieldContinuationDatas } from "./fieldContinuationDataQueries";
import { bulkRetrieveFieldTemplatesByFormTemplate } from "./fieldTemplateQueries";
import { bulkRetrieveOptionsByFormTemplateId } from "./optionQueries";
import { getTemplateData } from "db/merge";
import { getSingleFormTemplateDoc } from "utils/getSingleFormTemplateDoc";

interface TemplateJSON {
	/** The formTemplate documents we want to save to the database */
	formTemplates: FormTemplateForResolver[];
	/** The fieldTemplate documents we want to save to the database */
	fieldTemplates: FieldTemplateForResolver[];
	/** The option documents we want to save to the database */
	options: Option[];
	/** The fieldContinuationData documents we want to save to the database */
	continuationDatas: FieldContinuationDataForResolver[];
	/** The version number for the JSON format */
	v: number;
	/** the type of JSON */
	type: string;
}

/**
 * Grabs all formTemplates from indexedDb and constructs them
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The completely constructed formTemplates
 */
export async function buildFormTemplates() {
	// Grab all formTemplates and build them
	// Step 1: Get the raw documents
	// Step 2: Get all options and fieldTemplates we need
	// Step 3: Map the options out
	// Step 4: Map out fieldTemplates, assigning options. Seperate normal fieldTemplates from tableFieldTemplates
	// Step 5: Loop through normal fields, appending tableFields
	// Step 6: Assign the completed fieldTemplates to their formTemplate
	// Step 7: Return the formTemplates

	// Step 1: Get the raw documents
	const retrievedFormTemplates = await getAllFormTemplates();
	const formTemplateIdToFormTemplate = new Map();
	const formTemplateIds = retrievedFormTemplates.map((formTemplate) => {
		formTemplateIdToFormTemplate.set(formTemplate.id, formTemplate);
		return formTemplate.id;
	});

	// Step 2: Get all options and fieldTemplates we need
	const optionPromiseArrays = [];
	const fieldTemplatePromiseArrays = [];
	for (const id of formTemplateIds) {
		optionPromiseArrays.push(bulkRetrieveOptionsByFormTemplateId(id));
		fieldTemplatePromiseArrays.push(bulkRetrieveFieldTemplatesByFormTemplate(id));
	}

	const [arrayOfOptionArrays, arrayOfFieldTemplateArrays] = await Promise.all([
		await Promise.all(optionPromiseArrays),
		await Promise.all(fieldTemplatePromiseArrays),
	]);

	// Step 3: Map the options out
	const fieldTemplateIdToOptions: Map<string, Option[]> = new Map();

	for (const optionArray of arrayOfOptionArrays) {
		for (const option of optionArray) {
			const options = fieldTemplateIdToOptions.get(option.fieldTemplateId);
			if (options) {
				fieldTemplateIdToOptions.set(option.fieldTemplateId, [...options, { ...option }]);
			} else {
				fieldTemplateIdToOptions.set(option.fieldTemplateId, [{ ...option }]);
			}
		}
	}

	const finalFormTemplates: FormTemplate[] = retrievedFormTemplates;

	for (let i = 0; i < arrayOfFieldTemplateArrays.length; i++) {
		// Step 4: Map out fieldTemplates, assigning options. Seperate normal fieldTemplates from tableFieldTemplates
		const fieldTemplateArray = arrayOfFieldTemplateArrays[i];
		const tableFieldIdToTableFields = new Map();
		const normalFieldTemplates = [];
		for (const fieldTemplate of fieldTemplateArray) {
			const fieldTemplateCopy: FieldTemplate = { ...fieldTemplate };
			const options = fieldTemplateIdToOptions.get(fieldTemplateCopy.id);
			if (options) {
				fieldTemplateCopy.options = { items: options };
			}
			if (fieldTemplateCopy.tableField) {
				if (tableFieldIdToTableFields.has(fieldTemplateCopy.tableField)) {
					tableFieldIdToTableFields.set(fieldTemplateCopy.tableField, [
						...tableFieldIdToTableFields.get(fieldTemplateCopy.tableField),
						fieldTemplateCopy,
					]);
				} else {
					tableFieldIdToTableFields.set(fieldTemplateCopy.tableField, [fieldTemplateCopy]);
				}
			} else {
				normalFieldTemplates.push(fieldTemplateCopy);
			}
		}
		// Step 5: Loop through normal fields, appending tableFields
		const finalFieldTemplates = normalFieldTemplates.map((fieldTemplate) => {
			const fieldTemplateCopy = { ...fieldTemplate };
			if (tableFieldIdToTableFields.has(fieldTemplateCopy.id)) {
				fieldTemplateCopy.tableFields = { items: tableFieldIdToTableFields.get(fieldTemplateCopy.id) };
			}
			return fieldTemplateCopy;
		});
		// Step 6: Assign the completed fieldTemplates to their formTemplate
		finalFormTemplates[i].fieldTemplates = { items: finalFieldTemplates };
	}

	// Step 7: Return the formTemplates
	return finalFormTemplates;
}

/**
 * Queries the indexeddb for all formTemplate documents by their ids
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids The ids of the formTemplates we want to fetch
 * @returns The array of formTemplates corresponding to the provided ids
 */
export async function bulkRetrieveFormTemplates(ids: string[]) {
	const retrievedFormTemplates = await db.formTemplate.bulkGet(ids);
	const formTemplates = retrievedFormTemplates.filter((ft): ft is FormTemplateForResolver => ft !== undefined);
	return formTemplates;
}

/**
 * Given an array of formTemplates, saves them to the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplates The formTemplates we want to save to the indexedDb
 */
export async function bulkSaveFormTemplates(formTemplates: FormTemplateForResolver[]): Promise<void> {
	await db.formTemplate.bulkPut(formTemplates);
}

export async function checkIfTemplatesSaved() {
	const counts = await Promise.all([db.formTemplate.count(), db.fieldTemplate.count()]);
	return counts.every((count) => count > 0);
}

/**
 * Given a formTemplate, parses it out to and returns it ready for the resolver
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplate The formTemplate we want to parse down
 * @returns The formTemplate without external references, ready for the resolver
 */
export function createFormTemplateForBatchResolver(formTemplate: FormTemplateForResolver | FormTemplate): FormTemplateForResolver {
	const finalFormTemplate: FormTemplateForResolver = {
		id: formTemplate.id,
		template: formTemplate.template,
		type: formTemplate.type,
		description: formTemplate.description,
		fieldsOrder: formTemplate.fieldsOrder,
		formTemplateGroup: formTemplate.formTemplateGroup,
		imagesPerPage: formTemplate.imagesPerPage,
		exportType: formTemplate.exportType,
		createdAt: formTemplate.createdAt,
		updatedAt: formTemplate.updatedAt,
		deleted: formTemplate.deleted,
		exportInstruction: formTemplate.exportInstruction,
		templateName: formTemplate.templateName,
		version: formTemplate.version,
		versionName: formTemplate.versionName,
		projectId: formTemplate.projectId,
		publish: formTemplate.publish,
		createdBy: formTemplate.createdBy,
		updatedBy: formTemplate.updatedBy,
		group: formTemplate.group,
		readGroups: [...formTemplate.readGroups],
	};
	return finalFormTemplate;
}

/**
 * Given 2 formTemplates, compares their version and updatedAt times to return the most up to date formTemplate. Returns new object to prevent bad references
 * @author Eric Webb <ewebb@factorearth.com>
 * @param donorFormTemplate The donor's formTemplate
 * @param receiverFormTemplate The receiver's formTemplate
 * @returns The most up-to-date formTemplate
 */
export function createMergedFormTemplate(donorFormTemplate: FormTemplateForResolver, receiverFormTemplate: FormTemplateForResolver): FormTemplateForResolver {
	const timeStamp = new Date();
	const donorUpdatedAt = new Date(donorFormTemplate.updatedAt);
	const receiverUpdatedAt = new Date(receiverFormTemplate.updatedAt);
	const donorVersion = donorFormTemplate.version ? new Date(donorFormTemplate.version.split("_")[1]) : 0;
	const receiverVersion = receiverFormTemplate.version ? new Date(receiverFormTemplate.version.split("_")[1]) : 0;
	if (donorVersion > receiverVersion) {
		const formTemplate: FormTemplateForResolver = {
			createdAt: donorFormTemplate.createdAt,
			createdBy: donorFormTemplate.createdBy || "",
			deleted: donorFormTemplate.deleted || false,
			description: donorFormTemplate.description || "",
			exportInstruction: donorFormTemplate.exportInstruction || "",
			exportType: donorFormTemplate.exportType || "",
			fieldsOrder: donorFormTemplate.fieldsOrder || [],
			formTemplateGroup: donorFormTemplate.formTemplateGroup || "",
			group: donorFormTemplate.group,
			readGroups: [...donorFormTemplate.readGroups],
			id: donorFormTemplate.id,
			imagesPerPage: donorFormTemplate.imagesPerPage || 0,
			projectId: donorFormTemplate.projectId,
			publish: donorFormTemplate.publish || false,
			template: donorFormTemplate.template || "",
			templateName: donorFormTemplate.templateName || "",
			type: donorFormTemplate.type || "default",
			updatedAt: donorFormTemplate.updatedAt || timeStamp.toISOString(),
			updatedBy: donorFormTemplate.updatedBy || "",
			version: donorFormTemplate.version || "",
			versionName: donorFormTemplate.versionName || "",
		};
		return formTemplate;
	} else if (receiverVersion > donorVersion) {
		const formTemplate: FormTemplateForResolver = {
			createdAt: receiverFormTemplate.createdAt,
			createdBy: receiverFormTemplate.createdBy || "",
			deleted: receiverFormTemplate.deleted || false,
			description: receiverFormTemplate.description || "",
			exportInstruction: receiverFormTemplate.exportInstruction || "",
			exportType: receiverFormTemplate.exportType || "",
			fieldsOrder: receiverFormTemplate.fieldsOrder || [],
			formTemplateGroup: receiverFormTemplate.formTemplateGroup || "",
			group: receiverFormTemplate.group,
			readGroups: [...receiverFormTemplate.readGroups],
			id: receiverFormTemplate.id || donorFormTemplate.id,
			imagesPerPage: receiverFormTemplate.imagesPerPage || 0,
			projectId: receiverFormTemplate.projectId,
			publish: receiverFormTemplate.publish || false,
			template: receiverFormTemplate.template || "",
			templateName: receiverFormTemplate.templateName || "",
			type: receiverFormTemplate.type || "default",
			updatedAt: receiverFormTemplate.updatedAt || timeStamp.toISOString(),
			updatedBy: receiverFormTemplate.updatedBy || "",
			version: receiverFormTemplate.version || "",
			versionName: receiverFormTemplate.versionName || "",
		};
		return formTemplate;
	}
	const evaluation = donorUpdatedAt > receiverUpdatedAt;
	const formTemplate: FormTemplateForResolver = {
		createdAt: evaluation ? donorFormTemplate.createdAt : receiverFormTemplate.createdAt,
		createdBy: (evaluation ? donorFormTemplate.createdBy : receiverFormTemplate.createdBy) || "",
		deleted: (evaluation ? donorFormTemplate.deleted : receiverFormTemplate.deleted) || false,
		description: (evaluation ? donorFormTemplate.description : receiverFormTemplate.description) || "",
		exportInstruction: (evaluation ? donorFormTemplate.exportInstruction : receiverFormTemplate.exportInstruction) || "",
		exportType: (evaluation ? donorFormTemplate.exportType : receiverFormTemplate.exportType) || "",
		fieldsOrder: (evaluation ? donorFormTemplate.fieldsOrder : receiverFormTemplate.fieldsOrder) || [],
		formTemplateGroup: (evaluation ? donorFormTemplate.formTemplateGroup : receiverFormTemplate.formTemplateGroup) || "",
		group: evaluation ? donorFormTemplate.group : receiverFormTemplate.group,
		readGroups: evaluation ? [...donorFormTemplate.readGroups] : [...receiverFormTemplate.readGroups],
		id: evaluation ? donorFormTemplate.id : receiverFormTemplate.id,
		imagesPerPage: (evaluation ? donorFormTemplate.imagesPerPage : receiverFormTemplate.imagesPerPage) || 0,
		projectId: evaluation ? donorFormTemplate.projectId : receiverFormTemplate.projectId,
		publish: (evaluation ? donorFormTemplate.publish : receiverFormTemplate.publish) || false,
		template: (evaluation ? donorFormTemplate.template : receiverFormTemplate.template) || "",
		templateName: (evaluation ? donorFormTemplate.templateName : receiverFormTemplate.templateName) || "",
		type: (evaluation ? donorFormTemplate.type : receiverFormTemplate.type) || "default",
		updatedAt: (evaluation ? donorFormTemplate.updatedAt : receiverFormTemplate.updatedAt) || timeStamp.toISOString(),
		updatedBy: (evaluation ? donorFormTemplate.updatedBy : receiverFormTemplate.updatedBy) || "",
		version: (evaluation ? donorFormTemplate.version : receiverFormTemplate.version) || "",
		versionName: (evaluation ? donorFormTemplate.versionName : receiverFormTemplate.versionName) || "",
	};
	return formTemplate;
}

/**
 * Given the formTemplate id, deletes the formTemplate
 * @author Eric Webb <ewebb@factorearth.com>
 * @param formTemplateId The id of the formTemplate
 */
export async function deleteFormTemplateById(formTemplateId: string): Promise<void> {
	await db.formTemplate.delete(formTemplateId);
}

/**
 * Retrieves all formTemplates in the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns All formTemplates in the indexedDb
 */
export async function getAllFormTemplates() {
	const formTemplates = await db.formTemplate.toArray();
	return formTemplates;
}

/**
 * Given a formTemplate id, retrieves it from the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The id of the formTemplate
 * @returns The formTemplate if retrieved, or undefined
 */
export async function getFormTemplateById(id: string): Promise<FormTemplateForResolver | undefined> {
	const formTemplate = await db.formTemplate.get(id);
	return formTemplate;
}

/**
 * Queries the count of all FormTemplates, multiplies the count by the average size, and returns that value
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The filesize, in bytes, of all formTemplates
 */
export async function getTotalFormTemplateDiskSize() {
	const number = await db.formTemplate.count();
	const AVERAGE_DOCUMENT_SIZE = 391.5;
	return number * AVERAGE_DOCUMENT_SIZE;
}

/**
 * Saves the form template document to the offline indexed db.
 * @param formTemplate that needs to be saved.
 */
export async function putFormTemplate(formTemplate: FormTemplateForResolver): Promise<void> {
	try {
		await db.formTemplate.put(formTemplate);
	} catch (error) {
		recordSentry({ error, msg: "error saving form template offline:" });
	}
}

/**
 * Gets a full form template with built field templates from the offline db, (not ordered)
 * @param id of the form template that is needed
 * @returns a full form template from offline db.
 */
export async function getFullFormTemplateOffline(id: string): Promise<FormTemplate | void> {
	try {
		// First get all the fieldTemplates, options
		const [formTemplate, fieldTemplates, options] = await Promise.all([
			getFormTemplateById(id),
			bulkRetrieveFieldTemplatesByFormTemplate(id),
			bulkRetrieveOptionsByFormTemplateId(id),
		]);
		if (!formTemplate) {
			throw new Error(`NO FORM TEMPLATE FOUND WITH ID ${id}`);
		}

		// now just have to build out the map for options and fieldContinuationData
		const formTemplateMap: { options: { [fieldTemplateId: string]: Option[] } } = { options: {} };
		const builtFieldTemplates = [];
		for (let i = 0; i < options.length; i++) {
			const option = options[i];
			if (formTemplateMap.options[option.fieldTemplateId]) {
				formTemplateMap.options[option.fieldTemplateId].push(option);
			} else {
				formTemplateMap.options[option.fieldTemplateId] = [option];
			}
		}

		const contDataids: string[] = [];
		const tableFieldMap: Map<string, FieldTemplate[]> = new Map();
		// An unfortunately needed second loop through the fieldTemplates. The query above gets all fieldTemplates in a formGroup, which includes the table fields. It is faster to loop
		// through these than it is to make another query for them, and because we can't bulk grab fieldCOntinuationDatas by formTemplate, we need to get them somewhere. Doing this allows
		// that grab to be a bulk grab by their ids, which is better than n individual queries. We at least can filter, which I think makes this O(n + m) rather than O(n^2)
		const filteredFieldTemplates = fieldTemplates.filter((fieldTemplate) => {
			if (fieldTemplate.continuationDataId) contDataids.push(fieldTemplate.continuationDataId);
			if (fieldTemplate.tableField) {
				const tableFieldArray = tableFieldMap.get(fieldTemplate.tableField);
				if (tableFieldArray) {
					tableFieldArray.push(fieldTemplate);
				} else {
					tableFieldMap.set(fieldTemplate.tableField, [fieldTemplate]);
				}
			} else {
				return fieldTemplate;
			}
		});
		const continuationDatas = await bulkGetFieldContinuationDatas(contDataids);
		const contDataMap: Map<string, FieldContinuationDataForResolver> = new Map();
		for (const continuationData of continuationDatas) {
			contDataMap.set(continuationData.id, continuationData);
		}
		for (let i = 0; i < filteredFieldTemplates.length; i++) {
			const fieldTemplate: FieldTemplate = { ...filteredFieldTemplates[i] };
			// if there is an option order, there are options.
			if (fieldTemplate.optionsOrder && fieldTemplate.optionsOrder.length > 0) {
				let optionsArray = formTemplateMap.options[fieldTemplate.id];
				if (!optionsArray) {
					optionsArray = [];
				}
				fieldTemplate.options = { items: formTemplateMap.options[fieldTemplate.id] };
			}

			if (fieldTemplate.continuationDataId) {
				const continuationData = contDataMap.get(fieldTemplate.continuationDataId);
				if (continuationData) fieldTemplate.continuationData = continuationData;
			}

			if (fieldTemplate.inputType === "table" || fieldTemplate.inputType === "imageroll") {
				const builtTableFields = [];
				const tableFields = tableFieldMap.get(fieldTemplate.id);
				if (tableFields) {
					for (let j = 0; j < tableFields.length; j++) {
						const tableField: FieldTemplate = { ...tableFields[j] };
						if (tableField.optionsOrder && tableField.optionsOrder.length > 0) {
							tableField.options = { items: formTemplateMap.options[tableField.id] };
						}

						if (tableField.continuationDataId) {
							const tableFieldContData = contDataMap.get(tableField.continuationDataId);
							if (tableFieldContData) tableField.continuationData = tableFieldContData;
						}

						builtTableFields.push(tableField);
					}

					fieldTemplate.tableFields = { items: builtTableFields };
				}
			}
			builtFieldTemplates.push(fieldTemplate);
		}
		const finalFormTemplate: FormTemplate = { ...formTemplate };
		finalFormTemplate.fieldTemplates = { items: builtFieldTemplates };

		return finalFormTemplate;
	} catch (error) {
		recordSentry({ error, msg: "ERROR GETTING FULL FORMTEMPLATE OFFLINE:" });
	}
}

/**
 * Grabs a single form template and puts it in JSON Format for your uploading pleasure.
 * @param id string of the form template you wish to retrieve in JSON format
 * @param online boolean of destiny
 * @returns the template in JSON format
 */
export async function downloadFormTemplateJSON(id: string, online: boolean): Promise<TemplateJSON | undefined> {
	const fieldTemplates: FieldTemplate[] = [];
	const options: Option[] = [];
	const fieldContinuationDatas: FieldContinuationData[] = [];
	const formTemplates = [];

	await getTemplateData(id, online, fieldTemplates, fieldContinuationDatas, options);

	const formTemplateDoc = online ? await getSingleFormTemplateDoc(id) : await getFormTemplateById(id);
	if (formTemplateDoc) {
		formTemplates.push(formTemplateDoc);
	}

	return {
		continuationDatas: fieldContinuationDatas,
		fieldTemplates,
		options,
		formTemplates,
		v: 1,
		type: "FormTemplateJSON",
	};
}
